import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';

import { HttpHeaders, HttpParams } from '@angular/common/http';

import { LocaleCode } from '@language';
import { ShPaginationService } from '../../http/services/pagination.service';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { RecordType, RecordTypeStructure } from '../../records/entities/metadata/metadata.entity';
import { ApiRecordType, PublicationPolicy, RecordTypeInformationPackage } from '../entities/api-record-type.entity';
import { getRecordTypeOptionLabel } from '../helper/record-type-label.helper';

@Injectable({ providedIn: 'root' })
export class RecordTypesService {
    private readonly recordTypesEndpoint: string = 'v2/record-types';

    private readonly RECORD_TYPE_INFORMATION_PACKAGE_WHITELIST_DISPLAY: RecordTypeInformationPackage[] = [
        RecordTypeInformationPackage.Aip,
        RecordTypeInformationPackage.Sip
    ];

    private readonly RECORD_TYPE_INFORMATION_PACKAGE_WHITELIST_CREATE: RecordTypeInformationPackage[] = [
        RecordTypeInformationPackage.Aip,
        RecordTypeInformationPackage.Sip
    ];

    private readonly RECORD_TYPE_STRUCTURE_WHITELIST: RecordTypeStructure[] = [
        RecordTypeStructure.Data,
        RecordTypeStructure.DataFlat,
        RecordTypeStructure.Intellectual
    ];

    private allRecordTypes: ApiRecordType[] = [];
    private overviewRecordTypes: RecordType[] = [];
    private apiRecordTypes: ApiRecordType[] = [];
    private activeApiRecordTypes: ApiRecordType[] = [];
    private classificationType: ApiRecordType | undefined;

    constructor(private readonly paginationService: ShPaginationService) {}

    resolve(): Observable<ApiRecordType[]> {
        return this.initRecordTypes();
    }

    // #region init

    private initRecordTypes(): Observable<ApiRecordType[]> {
        const recordTypes$ = this.paginationService
            .getAll<ApiRecordType>(this.recordTypesEndpoint, new HttpParams(), new HttpHeaders())
            .pipe(take(1), shareReplay());

        recordTypes$.subscribe(apiRecordTypes => {
            this.allRecordTypes = apiRecordTypes;
            this.apiRecordTypes = this.filterWhitelistedRecordTypes(apiRecordTypes, false);
            this.activeApiRecordTypes = this.apiRecordTypes.filter(recordType => recordType.Active);

            // Never filter out the inactive types here!
            this.overviewRecordTypes = this.filterWhitelistedRecordTypes(apiRecordTypes).map(apiRecordType =>
                this.apiRecordTypeToRecordType(apiRecordType)
            );

            this.classificationType = apiRecordTypes.find(
                recordType => recordType.Structure === RecordTypeStructure.Classification
            );
        });

        return recordTypes$;
    }

    // #endregion

    // #region GET

    /**
     * @returns Array of RecordTypes relevant for MediaHaven overviews
     */
    public getOverviewRecordTypes(): RecordType[] {
        return this.overviewRecordTypes;
    }

    public getApiRecordTypes(): ApiRecordType[] {
        return this.apiRecordTypes;
    }

    public getActiveRecordTypes(): ApiRecordType[] {
        return this.activeApiRecordTypes;
    }

    public getApiOverViewTypesByOrg(organisationId?: OrganisationId): Observable<ApiRecordType[]> {
        return this.paginationService
            .getAll<ApiRecordType>(
                this.recordTypesEndpoint,
                organisationId ? new HttpParams().set('organisationId', organisationId) : new HttpParams(),
                new HttpHeaders()
            )
            .pipe(
                take(1),
                map(types => this.filterWhitelistedRecordTypes(types))
            );
    }

    public getOverViewTypesByOrg(organisationId?: OrganisationId): Observable<RecordType[]> {
        return this.getApiOverViewTypesByOrg(organisationId).pipe(
            map(types => types.map(apiRecordType => this.apiRecordTypeToRecordType(apiRecordType)))
        );
    }

    /**
     * @returns List of record types that can be a direct child of a classification
     */
    public getTopLevelTypes(): ApiRecordType[] {
        return this.classificationType
            ? this.apiRecordTypes.filter(recordType =>
                  this.classificationType!.AllowedChildren.includes(recordType.Name)
              )
            : this.apiRecordTypes;
    }

    /**
     * @returns List of record types that can be the parent of another type
     */
    public getParentTypes(): ApiRecordType[] {
        return this.activeApiRecordTypes.filter(
            recordType =>
                !!recordType.AllowedChildren &&
                recordType.AllowedChildren.length > 0 &&
                recordType.InformationPackage === RecordTypeInformationPackage.Aip
        );
    }

    /**
     *
     * @param childType The record type for which to get the parents
     * @returns Returns a list of active record types of which provided record type can be a child
     */
    public getParentsOfType(childType: RecordType): ApiRecordType[] {
        return this.activeApiRecordTypes.filter(recordType => recordType.AllowedChildren.includes(childType));
    }

    /**
     *
     * @param childTypes The record types for which to get the parents
     * @returns Returns a list of active record types of which all provided record types can be a child
     */
    public getParentsOfTypes(childTypes: RecordType[]): ApiRecordType[] {
        if (childTypes.length === 0) {
            return [];
        }

        const childMainTypes = childTypes.map(childType => childType.split('.')[0]);
        let parentTypes: ApiRecordType[] = this.getParentsOfType(childMainTypes[0] as RecordType);

        childMainTypes.slice(1).forEach(childType => {
            const currentTypes: ApiRecordType[] = this.getParentsOfType(childType as RecordType);
            parentTypes = parentTypes.filter(parentType =>
                currentTypes.find(currentType => currentType.Name === parentType.Name)
            );
        });

        return parentTypes;
    }

    /**
     *
     * @param recordType
     * @returns Returns the active allowed children of the provided recordType. Returns an empty list if the type cannot be found
     */
    public getAllowedChildren(recordType: RecordType): ApiRecordType[] {
        const allowedChildren: RecordType[] =
            this.activeApiRecordTypes.find(apiType => apiType.Name === this.getMainRecordType(recordType))
                ?.AllowedChildren ?? [];

        return allowedChildren
            .map(childType => this.activeApiRecordTypes.find(apiType => apiType.Name === childType)!)
            .filter(type => !!type); // It's possible that allowed_children contains types that have been filtered out previously;
    }

    /**
     *
     * @param recordType
     * @returns Returns true if the provided recordtype is a top level type (type can be a child of a classification)
     */
    public isTopLevelType(recordType: RecordType): boolean {
        return !!this.classificationType?.AllowedChildren.includes(this.getMainRecordType(recordType));
    }

    /**
     *
     * @param recordType
     * @returns returns the full RecordType object for the provided recordType
     */
    public getRecordTypeInfo(recordType: RecordType): ApiRecordType | undefined {
        return this.apiRecordTypes.find(apiRecordType => apiRecordType.Name === this.getMainRecordType(recordType));
    }

    /**
     *
     * @param recordType
     * @param policy
     * @returns returns true if the provided recordType has the specified publication policy
     */
    public hasPublicationPolicy(recordType: RecordType, policy: PublicationPolicy): boolean {
        return this.getRecordTypeInfo(recordType)?.PublicationPolicy?.includes(policy) ?? false;
    }
    // #endregion

    // #region Util

    public getRecordTypeTranslation(recordTypeName: string, language: LocaleCode): string {
        return this.getRecordTypeTranslations([recordTypeName], language)[0];
    }

    public getRecordTypeTranslations(recordTypeNames: string[], language: LocaleCode): string[] {
        const recordTypes: ApiRecordType[] = recordTypeNames
            .map(recordTypeName => {
                const matchingRecordType = this.allRecordTypes.find(
                    apiRecordType => apiRecordType.Name === recordTypeName
                );

                if (!matchingRecordType) {
                    console.error(`RecordTypesService:: No definition found for record-type ${recordTypeName}`);
                    return null;
                }

                return matchingRecordType;
            })
            .filter((recordType): recordType is ApiRecordType => !!recordType);

        return recordTypes.map(recordType => getRecordTypeOptionLabel(recordType, language, recordTypes));
    }

    /**
     * @param apiRecordTypes
     * @returns Array of ApiRecordTypes relevant for MediaHaven
     */
    private filterWhitelistedRecordTypes(apiRecordTypes: ApiRecordType[], display = true): ApiRecordType[] {
        return apiRecordTypes
            .filter(apiRecordType =>
                display
                    ? this.RECORD_TYPE_INFORMATION_PACKAGE_WHITELIST_DISPLAY.includes(apiRecordType.InformationPackage)
                    : this.RECORD_TYPE_INFORMATION_PACKAGE_WHITELIST_CREATE.includes(apiRecordType.InformationPackage)
            )
            .filter(apiRecordType => this.RECORD_TYPE_STRUCTURE_WHITELIST.includes(apiRecordType.Structure));
    }

    /**
     * Convert ApiRecordType object to RecordType
     * @param apiRecordType
     * @returns RecordType
     */
    private apiRecordTypeToRecordType(apiRecordType: ApiRecordType): RecordType {
        return apiRecordType.Name as RecordType;
    }

    private getMainRecordType(recordType: RecordType): RecordType {
        return recordType.split('.')[0] as RecordType;
    }
    // #endregion
}
